import { SuperInput } from "@components:SuperInput/SuperInput";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { memo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MdAdd, MdDelete } from "react-icons/md";

import { useFieldName, useSchemaForm } from "../context";
import { MapNode } from "../types";
import { FieldLabel } from "./FieldLabel";
import { PrimitiveInput } from "./PrimitiveInput";

/*
 * An object with free keys (e.g. language code -> model name).
 * Keys are committed on blur so typing one does not move the row around.
 * With `bare`, no label (the parent shows the title).
 */
export const MapField = memo(
  ({ node, bare = false }: { node: MapNode; bare?: boolean }) => {
    const { t } = useTranslation();
    const { errors } = useSchemaForm();
    const { control, setValue } = useFormContext();
    const name = useFieldName(node.path);
    const watched = useWatch({ control, name });
    const entries: Record<string, unknown> =
      watched && typeof watched === "object" ? watched : {};
    const [editingKeys, setEditingKeys] = useState<Record<string, string>>({});

    const update = (next: Record<string, unknown>) =>
      setValue(name, next, { shouldDirty: true });

    const add = () => {
      let n = Object.keys(entries).length + 1;
      while (`key${n}` in entries) n += 1;
      update({ ...entries, [`key${n}`]: "" });
    };

    const remove = (key: string) => {
      const next = { ...entries };
      delete next[key];
      update(next);

      const editing = { ...editingKeys };
      delete editing[key];
      setEditingKeys(editing);
    };

    const rename = (oldKey: string, newKey: string) => {
      const editing = { ...editingKeys };
      delete editing[oldKey];
      setEditingKeys(editing);

      if (oldKey === newKey || !newKey || newKey in entries) return;

      update(
        Object.fromEntries(
          Object.entries(entries).map(([key, value]) =>
            key === oldKey ? [newKey, value] : [key, value],
          ),
        ),
      );
    };

    return (
      <Box sx={{ mb: 2 }}>
        {!bare && (
          <FieldLabel
            title={node.title}
            description={node.description}
            required={node.required}
          />
        )}

        {Object.entries(entries).map(([key, value]) => {
          const displayKey = editingKeys[key] ?? key;
          const isDuplicate = displayKey !== key && displayKey in entries;
          const itemPath = node.path ? `${node.path}.${key}` : key;

          return (
            <Box key={key}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <SuperInput.TextField
                  value={displayKey}
                  placeholder={t("schemaForm.keyPlaceholder")}
                  onChange={(e) =>
                    setEditingKeys({ ...editingKeys, [key]: e.target.value })
                  }
                  onBlur={(e) => rename(key, e.target.value)}
                  errorText={
                    isDuplicate
                      ? t("schemaForm.keyExists", { key: displayKey })
                      : undefined
                  }
                  slotProps={{
                    htmlInput: { "aria-label": t("schemaForm.keyPlaceholder") },
                  }}
                  sx={{ flex: 1 }}
                />
                <PrimitiveInput
                  node={node.value}
                  value={value}
                  onChange={(next) => update({ ...entries, [key]: next })}
                  label={key}
                  placeholder={t("schemaForm.valuePlaceholder")}
                  errorText={errors[itemPath]}
                  sx={{ flex: 5 }}
                />
                <IconButton
                  size="medium"
                  color="error"
                  aria-label={t("common.delete")}
                  onClick={() => remove(key)}
                >
                  <MdDelete />
                </IconButton>
              </Box>
            </Box>
          );
        })}

        {errors[node.path] && (
          <Typography variant="caption" color="error" sx={{ display: "block" }}>
            {errors[node.path]}
          </Typography>
        )}

        <Button
          startIcon={<MdAdd />}
          size="small"
          onClick={add}
          sx={{ mt: -1 }}
        >
          {t("schemaForm.addEntry")}
        </Button>
      </Box>
    );
  },
);

MapField.displayName = "MapField";
